const MAX_AVATAR_SIZE = 640;
const MAX_INPUT_BYTES = 8 * 1024 * 1024;
const MAX_OUTPUT_BYTES = 1024 * 1024;

/** Always encode a small, browser-readable JPEG. Never upload the original on decode failure. */
export async function prepareAvatar(file: File): Promise<File> {
  if (file.size > MAX_INPUT_BYTES) throw new Error("Choose a photo smaller than 8 MB.");
  if (!file.type.startsWith("image/")) throw new Error("Choose a JPG, PNG or WebP photo.");
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("This photo could not be converted. Choose a JPG, PNG or WebP copy."));
      img.src = objectUrl;
    });
    if (!image.naturalWidth || !image.naturalHeight) throw new Error("The selected photo could not be read.");
    const scale = Math.min(1, MAX_AVATAR_SIZE / image.naturalWidth, MAX_AVATAR_SIZE / image.naturalHeight);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("The selected photo could not be converted.");
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.84));
    if (!blob || blob.type !== "image/jpeg" || blob.size === 0 || blob.size > MAX_OUTPUT_BYTES) {
      throw new Error("The selected photo could not be converted. Try a smaller JPG, PNG or WebP photo.");
    }
    return new File([blob], "avatar.jpg", { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
